import ChartMetric from '@/Enums/Chart/Metric.enum';
import InsightCallout from '@/components/InsightCallout';
import { getVerticalFootTrafficGraphData } from '@/helpers/attribution/chartData';
import { selectFootTrafficLoyaltyInsight } from '@/helpers/attribution/footTrafficInsights';
import { downloadCSV } from '@/helpers/downloadCSV';
import { useAppSelector } from '@/store/hooks';
import { selectFootTrafficFreqReport } from '@/store/selectors/attribution/footTraffic.selectors';
import { Box, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import VerticalChart from '../Charts/VerticalChart';
import GraphCard from './BaseCard/GraphCard';

const label = 'Visit Frequency';

// Buckets considered "loyal" — used for the callout
const LOYAL_BUCKETS = [
  '5 visits',
  '6 visits',
  '7 visits',
  '8 visits',
  '9 visits',
  '10 visits',
  '15 visits',
  '20 visits',
  '30+ visits',
];

const VisitFrequencyCard = () => {
  const { loading, freqReport } = useAppSelector(selectFootTrafficFreqReport);
  const { text: insightText } = useAppSelector(selectFootTrafficLoyaltyInsight);
  const { segments, avgFrequency, repeatVisitorPercent } = freqReport;

  // Source segments are 0..1 fractions; the chart shows them as percent values
  const displaySegments = useMemo(
    () => Object.fromEntries(Object.entries(segments).map(([k, v]) => [k, Math.round(v * 1000) / 10])),
    [segments],
  );

  const { chartData, csvData } = useMemo(
    () => getVerticalFootTrafficGraphData(displaySegments, loading),
    [displaySegments, loading],
  );

  const noData = useMemo<boolean>(() => Object.values(segments).every(p => !p), [segments]);

  const loyalPercent = useMemo(
    () => LOYAL_BUCKETS.reduce((acc, bucketLabel) => acc + (segments[bucketLabel] ?? 0), 0),
    [segments],
  );

  const onDownload = useCallback(() => {
    if (!loading) {
      downloadCSV(csvData, label);
    }
  }, [csvData, loading]);

  return (
    <GraphCard label={label} loading={loading} noData={noData} onDownload={onDownload} allowPrint>
      <Box sx={{ display: 'flex', gap: '30px', mb: 2 }}>
        <Box>
          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>Repeat Visitor %</Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 600 }}>{(repeatVisitorPercent * 100).toFixed(1)}%</Typography>
        </Box>
        <Box>
          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>Avg Frequency</Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 600 }}>{avgFrequency.toFixed(2)}</Typography>
        </Box>
      </Box>
      {!noData && (
        <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 1 }}>
          {(loyalPercent * 100).toFixed(1)}% of visitors came back 5+ times this period
        </Typography>
      )}
      <VerticalChart chartData={chartData} legendLabel={ChartMetric.FootTrafficVisits} />
      <InsightCallout text={insightText} loading={loading} />
    </GraphCard>
  );
};

export default VisitFrequencyCard;
